import "./EmbeddableContent.scss";

import React from "react";

import { Script } from "gatsby";

import useFeatureToggle from "../../hooks/use-feature-toggle/useFeatureToggle";
import {
  DocumentCardData,
  EmbeddableContentData,
  FeatureToggle
} from "../../models/app-data-model";
import { sanitizeScriptTag } from "../../services/script-tag-sanitizer/script-tag-sanitizer";
import DocumentCard from "../document-card/DocumentCard";
import Video from "../video/Video";

export type EmbeddableContentProps = {
  embedContentData: EmbeddableContentData;
  featureToggles: FeatureToggle[];
};

type JSONValue = string | number | boolean | { [x: string]: JSONValue } | JSONValue[];
 
// function to search JSON for an element, in this case href. 
function findFirstInstance(json: JSONValue, key: string): JSONValue | undefined {
  if (typeof json === 'object' && json !== null) {
    if (Array.isArray(json)) {
      for (const item of json) {
        const result = findFirstInstance(item, key);
        if (result !== undefined) {
          return result;
        }
      }
    } else {
      for (const k in json) {
        if (k === key) {
          return json[k];
        }
        const result = findFirstInstance(json[k], key);
        if (result !== undefined) {
          return result;
        }
      }
    }
  }
  return undefined;
}

const EmbeddableContent: React.FC<EmbeddableContentProps> = ({ embedContentData, featureToggles }: EmbeddableContentProps) => {
  const isVideo = embedContentData.reference.length > 0 && 'ph_videos' === embedContentData.reference[0]?.internal?.type;
  const isEmbed = embedContentData.reference.length > 0 && 'ph_embeds' === embedContentData.reference[0]?.internal?.type;
  let isDocument = embedContentData.reference.length > 0 && 'ph_document' === embedContentData.reference[0]?.internal?.type;

  const isEnabled = isEmbed && embedContentData.reference[0]?.enabled;
  const isGlobalEmbedScriptEnabled = useFeatureToggle('global-embed-script', featureToggles);

  let documentData: DocumentCardData = {
    button_label: "",
    button_url: "",
    short_description: "",
    title: ""
  };

  if (isDocument) {
    const title = embedContentData.reference[0]?.title;
    const manual_link = embedContentData.reference[0]?.manual_link;
    const short_description = embedContentData.reference[0]?.short_description;

    if (embedContentData.reference[0]?.is_this_document_in_content_hub) {
      const contentAsset = embedContentData.reference[0]?.contenthub_asset[0];

      // seems to be original or downloadOriginal...?
       // find the first href. Then we don't have to care if it is 
       // contentAsset.public_link?.pdf[0] or contentAsset.public_link.original[0]
       // ... unless we do, which still isn't clear.
      let url = findFirstInstance(contentAsset, "href")?.toString();

      if(!url)
      {
        url="";
      }

      documentData = {
        button_label: title ? title : contentAsset.properties.Title,
        button_url: url,
        short_description: short_description,
        title: title ? title : contentAsset.properties.Title,
      }

    }
    else if (manual_link && manual_link.length > 0) {
      documentData = {
        button_label: title ? title : 'document',
        button_url: manual_link,
        short_description: short_description,
        title: title ? title : 'document',
      }
    }
    else {
      isDocument = false;
    }
  }


  return (
    <>
      {isGlobalEmbedScriptEnabled &&
        <div className={'embeddable-content'}>
          {isVideo && embedContentData.reference[0].title && embedContentData.reference[0].video_link &&
            <Video title={embedContentData.reference[0].title} url={embedContentData.reference[0].video_link}></Video>}
          {isEmbed && isEnabled && embedContentData.reference[0]?.markup && <div dangerouslySetInnerHTML={{ __html: embedContentData.reference[0]?.markup }} />}
          {isEmbed && isEnabled && embedContentData.reference[0]?.script_source_url && <Script src={embedContentData.reference[0]?.script_source_url} />}
          {isEmbed && isEnabled && embedContentData.reference[0]?.source_code &&
            <Script strategy='idle' id={`${embedContentData.reference[0].uid}`}>
              {`${sanitizeScriptTag(embedContentData.reference[0]?.source_code)}`}
            </Script>
          }
          {isDocument &&
            <><DocumentCard DocumentCardData={documentData}></DocumentCard></>
          }
        </div>
      }
    </>
  );
};

export default EmbeddableContent;